/**
 * View All Header Component JavaScript
 * Handles interactive functionality for the view-all-header section
 */

class ViewAllHeader {
  constructor() {
    this.header = document.querySelector('.view-all-header');
    this.container = document.querySelector('.view-all-header__container');
    this.content = document.querySelector('.view-all-header__content');
    this.image = document.querySelector('.view-all-header__image');
    this.currentUrl = window.location.href;
    this.apiUrl = 'https://pim.earthpositiveonline.com/api/colours';
    
    if (this.header) {
      this.init();
    }
  }

  init() {
    // Always start with section hidden
    this.hideSection();
    
    // Process the current URL
    this.processUrlChange();
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Start monitoring URL changes
    this.startUrlMonitoring();
  }

  /**
   * Process URL change - main workflow
   */
  async processUrlChange() {
    // Step 1: Check if exactly one colour filter exists
    if (!this.hasExactlyOneColourFilter()) {
      this.hideSection();
      return;
    }

    // Step 2: Fetch API data (always refetch)
    const colourData = await this.fetchColourData();
    if (!colourData) {
      this.hideSection();
      return;
    }

    // Step 3: Find matching colour and validate image
    const matchedColour = this.findMatchingColour(colourData);
    if (!matchedColour) {
      this.hideSection();
      return;
    }

    // Step 4: Check if image is available
    const imageAvailable = await this.validateImage(matchedColour.showcase_image_url);
    if (!imageAvailable) {
      this.hideSection();
      return;
    }

    // Step 5: Update content and show section
    this.updateContent(matchedColour);
    this.showSection();
  }

  /**
   * Check if exactly one colour filter exists in query string
   */
  hasExactlyOneColourFilter() {
    const urlParams = new URLSearchParams(window.location.search);
    const colourFilters = [];
    
    for (const [key, value] of urlParams) {
      if (key === 'filter.p.m.custom.colour') {
        colourFilters.push(value);
      }
    }
    
    return colourFilters.length === 1;
  }

  /**
   * Fetch colour data from API (always fresh)
   */
  async fetchColourData() {
    try {
      const response = await fetch(this.apiUrl + '?t=' + Date.now()); // Cache busting
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching colour data:', error);
      return null;
    }
  }

  /**
   * Find matching colour in API data
   */
  findMatchingColour(colourData) {
    const currentColour = this.getCurrentColourFilter();
    
    if (!currentColour || !colourData || !colourData.data) {
      return null;
    }
    
    // Strategy 1: Direct match with name_encoded (this should work for most cases)
    let matched = colourData.data.find(colour => 
      colour.name_encoded && colour.name_encoded.en === currentColour
    );
    
    if (matched) {
      return matched;
    }
    
    // Strategy 2: Convert URL parameter to match API encoding format
    // Replace spaces with + signs to match the API's name_encoded format
    const encodedCurrentColour = decodeURIComponent(currentColour).replace(/ /g, '+');
    
    matched = colourData.data.find(colour => 
      colour.name_encoded && colour.name_encoded.en === encodedCurrentColour
    );
    
    if (matched) {
      return matched;
    }
    
    // Strategy 3: Match decoded URL parameter against regular name
    const decodedCurrentColour = decodeURIComponent(currentColour);
    
    matched = colourData.data.find(colour => 
      colour.name && colour.name.en === decodedCurrentColour
    );
    
    if (matched) {
      return matched;
    }
    
    // Strategy 4: Case-insensitive match as final fallback
    matched = colourData.data.find(colour => {
      const colourName = colour.name?.en?.toLowerCase();
      const encodedName = colour.name_encoded?.en?.toLowerCase();
      const searchName = decodedCurrentColour.toLowerCase();
      const searchEncoded = encodedCurrentColour.toLowerCase();
      
      return colourName === searchName || 
             encodedName === searchName || 
             encodedName === searchEncoded;
    });
    
    return matched;
  }

  /**
   * Validate that the image URL is accessible
   */
  async validateImage(imageUrl) {
    if (!imageUrl || !imageUrl.trim()) {
      return false;
    }

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = imageUrl.trim();
      
      // Timeout after 5 seconds
      setTimeout(() => resolve(false), 5000);
    });
  }

  /**
   * Update all content with matched colour data
   */
  updateContent(matchedColour) {
    // Update h3 with colour name
    const h3Element = this.content.querySelector('h3');
    if (h3Element) {
      h3Element.textContent = (matchedColour.name && matchedColour.name.en) || '';
    }
    
    // Update code paragraph
    const codeElement = this.content.querySelector('p:last-child');
    if (codeElement) {
      codeElement.textContent = matchedColour.code ? `Code: ${matchedColour.code}` : '';
    }
    
    // Update PMS paragraph
    const pmsElement = this.content.querySelector('p:nth-child(2)');
    if (pmsElement) {
      if (matchedColour.settings && matchedColour.settings[0] && matchedColour.settings[0].pms) {
        pmsElement.textContent = `PMS: ${matchedColour.settings[0].pms}`;
      } else {
        pmsElement.textContent = '';
      }
    }
    
    // Update background image
    if (this.image && matchedColour.showcase_image_url) {
      this.image.style.backgroundImage = `url('${matchedColour.showcase_image_url.trim()}')`;
    }
  }

  /**
   * Get current colour filter value
   */
  getCurrentColourFilter() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('filter.p.m.custom.colour');
  }

  /**
   * Show the section
   */
  showSection() {
    if (this.header) {
      this.header.classList.add('js-show');
      this.header.style.display = 'block';
    }
  }

  /**
   * Hide the section
   */
  hideSection() {
    if (this.header) {
      this.header.classList.remove('js-show');
      this.header.style.display = 'none';
    }
  }

  /**
   * Start monitoring URL changes
   */
  startUrlMonitoring() {
    // Listen for popstate events (back/forward buttons)
    window.addEventListener('popstate', () => this.handleUrlChange());
    
    // Listen for hashchange events
    window.addEventListener('hashchange', () => this.handleUrlChange());
    
    // Poll for URL changes (fallback for programmatic changes)
    setInterval(() => {
      if (window.location.href !== this.currentUrl) {
        this.handleUrlChange();
      }
    }, 500);
    
    // Listen for Shopify-specific events if available
    if (window.Shopify && window.Shopify.theme) {
      document.addEventListener('shopify:section:load', () => this.handleUrlChange());
      document.addEventListener('shopify:section:unload', () => this.handleUrlChange());
    }
    
    // Listen for custom filter events
    document.addEventListener('facet:changed', () => this.handleUrlChange());
    document.addEventListener('collection:filter:changed', () => this.handleUrlChange());
  }

  /**
   * Handle URL change
   */
  handleUrlChange() {
    if (window.location.href !== this.currentUrl) {
      this.currentUrl = window.location.href;
      this.processUrlChange();
    }
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    window.addEventListener('resize', () => this.handleResize());
  }

  /**
   * Handle window resize
   */
  handleResize() {
    // Add any resize-specific logic here if needed
  }

  /**
   * Cleanup method
   */
  destroy() {
    // Remove event listeners and cleanup
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new ViewAllHeader();
});

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (window.viewAllHeader) {
    window.viewAllHeader.destroy();
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ViewAllHeader;
}